import { useState, useMemo } from 'react';
import { Link } from '@tanstack/react-router';
import { MainLayout } from '../layouts/MainLayout';
import { RequestListItem } from '../components/RequestForm';
import { RequestWizard } from '../components/RequestWizard';
import { EditRequestModal } from '../components/EditRequestModal';
import { useRequests, useCancelRequest, useUserPlan } from '../hooks/useApi';
import { useRequireAuth } from '../hooks/useRequireAuth';
import { Loader2, AlertCircle, Bell, Plus, ArrowUpRight } from 'lucide-react';
import type { AvailabilityRequest } from '../types';

export function RequestsPage() {
  const { isLoading: isAuthLoading } = useRequireAuth();
  const { data, isLoading, error } = useRequests();
  const { data: plan } = useUserPlan();
  const cancelRequest = useCancelRequest();
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editingRequest, setEditingRequest] = useState<AvailabilityRequest | null>(null);

  const requests = useMemo(() => data || [], [data]);

  const activeCount = requests.filter((r) => r.isActive).length;
  const atLimit =
    plan &&
    Number.isFinite(plan.maxActiveAlerts) &&
    activeCount >= plan.maxActiveAlerts &&
    plan.tier === 'free';

  const groupedByPark = useMemo(() => {
    const groups: { placeId: string; placeName: string; requests: AvailabilityRequest[] }[] = [];
    const map = new Map<string, { placeName: string; requests: AvailabilityRequest[] }>();

    for (const req of requests) {
      const key = req.placeId || 'unknown';
      const name = req.placeName || 'Unknown Park';
      let group = map.get(key);
      if (!group) {
        group = { placeName: name, requests: [] };
        map.set(key, group);
      }
      group.requests.push(req);
    }

    for (const [placeId, { placeName, requests: reqs }] of map) {
      groups.push({ placeId, placeName, requests: reqs });
    }

    return groups;
  }, [requests]);

  if (isAuthLoading || isLoading) {
    return (
      <MainLayout>
        <div className="flex flex-col items-center justify-center py-16">
          <Loader2 className="w-10 h-10 animate-spin text-emerald-600 mb-4" />
          <p className="text-gray-600">Loading your requests...</p>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <div className="flex flex-col items-center justify-center py-16">
          <div className="p-4 bg-red-50 rounded-full mb-4">
            <AlertCircle className="w-10 h-10 text-red-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to load requests</h3>
          <p className="text-gray-600">{error.message}</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="max-w-4xl mx-auto">
        <div className="mb-8 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">My Availability Requests</h1>
            <p className="text-gray-600 mt-1">
              Create and manage campsite availability alerts.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setWizardOpen(true)}
            disabled={!!atLimit}
            className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-700 shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-4 h-4" />
            New Request
          </button>
        </div>

        {atLimit && (
          <div className="mb-6 p-4 rounded-lg border border-amber-200 bg-amber-50 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-800">
                You've reached the free plan limit of {plan.maxActiveAlerts} active alert.
              </p>
              <p className="text-sm text-amber-700 mt-1">
                Upgrade to Pro for unlimited alerts, Telegram notifications, and faster cooldowns.
              </p>
            </div>
            <Link
              to="/settings"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-600 text-white text-sm font-medium hover:bg-amber-700 shrink-0"
            >
              Upgrade
              <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        )}

        <RequestWizard
          open={wizardOpen}
          onClose={() => setWizardOpen(false)}
        />
        {editingRequest && (
          <EditRequestModal
            request={editingRequest}
            onClose={() => setEditingRequest(null)}
          />
        )}

        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Existing Requests</h2>
          <Link
            to="/search"
            search={{}}
            className="text-sm text-emerald-700 hover:text-emerald-800 font-medium"
          >
            Browse Search
          </Link>
        </div>

        {requests.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-gray-100 rounded-full mb-4">
              <Bell className="w-7 h-7 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No requests yet</h3>
            <p className="text-gray-600 max-w-md mx-auto">
              Use the form above to create your first availability alert.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {groupedByPark.map((group) => (
              <div key={group.placeId}>
                <h3 className="text-base font-semibold text-gray-800 mb-3">{group.placeName}</h3>
                <div className="space-y-3">
                  {group.requests.map((request) => (
                    <RequestListItem
                      key={request.id}
                      request={request}
                      onCancel={(id) => cancelRequest.mutate(id)}
                      onEdit={(req) => setEditingRequest(req)}
                      isCancelling={cancelRequest.isPending}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </MainLayout>
  );
}
